/**
 * RTWO Indexer Engine — Event Indexing Service
 */

import { INDEXER_SERVICE_IDS } from '../domain-models';
import type { IndexedEventId, RTWOIndexedEvent } from '../domain-models';
import type { EventIndexResult, IEventIndexingService } from '../service-interfaces';
import type { IndexerRepositories } from '../repositories/indexer-repositories';
import type { EventBusAdapterRegistry } from '../registry/adapter-registries';
import { IndexerInternalEventTypes } from '../events/indexer-event-bus';
import { BaseIndexerService, type IndexerServiceContext } from './base-indexer-service';

export class EventIndexingService extends BaseIndexerService implements IEventIndexingService {
  readonly serviceId = INDEXER_SERVICE_IDS.EVENT_INDEXING;
  readonly serviceName = 'Event Indexing Service';
  readonly description = 'Indexes blockchain events via Event Bus adapter. No fake events indexed.';
  readonly integrations = ['RPC Platform', 'Consensus Engine', 'Blockchain Protocol Engine'];
  readonly requiresRPCSource = true;
  readonly requiresIndexStorage = false;

  constructor(
    context: IndexerServiceContext,
    private readonly repositories: IndexerRepositories,
    private readonly eventBusRegistry: EventBusAdapterRegistry
  ) {
    super(context);
  }

  async indexEvent(transactionHash: string, blockNumber: number): Promise<EventIndexResult> {
    if (!this.eventBusRegistry.isConfigured()) {
      return {
        success: false,
        indexedEventId: null,
        error:
          'RPC Platform Not Connected — Cannot index events until real RPC source is connected.',
        isPlaceholder: true,
        source: 'Indexer Engine — RPC Platform Not Connected',
      };
    }

    const adapterResult = await this.eventBusRegistry
      .getActiveAdapter()
      .indexEvent(transactionHash, blockNumber);

    if (adapterResult?.event) {
      this.repositories.events.save(adapterResult.event);
      this.context.eventBus.emit(IndexerInternalEventTypes.EVENT_INDEXED, {
        indexedEventId: adapterResult.event.indexedEventId,
        transactionHash,
        blockNumber,
      });
    }

    return {
      success: false,
      indexedEventId: null,
      error: 'RPC Platform Not Connected — Cannot index events until real RPC source is connected.',
      isPlaceholder: true,
      source: 'Indexer Engine — RPC Platform Not Connected',
    };
  }

  getAllIndexedEvents(): RTWOIndexedEvent[] {
    if (!this.eventBusRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.events.findAll();
  }

  getIndexedEvent(indexedEventId: IndexedEventId): RTWOIndexedEvent | null {
    if (!this.eventBusRegistry.isConfigured()) {
      return null;
    }

    return this.repositories.events.findById(indexedEventId);
  }

  getIndexedEventCount(): 0 {
    return 0;
  }
}
